import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Save, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { contentAPI } from '../../services/api';

const SiteSettingsEditor = () => {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await contentAPI.get('site_settings');
      setData(res.data || {});
    } catch { toast.error('Failed to load'); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      await contentAPI.update('site_settings', data);
      toast.success('Site settings saved!');
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const update = (path, value) => {
    setData(prev => {
      const copy = JSON.parse(JSON.stringify(prev));
      const keys = path.split('.');
      let obj = copy;
      for (let i = 0; i < keys.length - 1; i++) {
        if (!obj[keys[i]]) obj[keys[i]] = {};
        obj = obj[keys[i]];
      }
      obj[keys[keys.length - 1]] = value;
      return copy;
    });
  };

  if (!data) return <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin" /></div>;

  return (
    <div className="space-y-6 max-w-4xl">
      <Card>
        <CardHeader><CardTitle>General Information</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <label className="text-sm font-medium text-gray-700 mb-1 block">Browser Tab Title (shown in Chrome tab)</label>
            <Input value={data.browserTitle || ''} onChange={e => update('browserTitle', e.target.value)} data-testid="browser-title-input" placeholder="SOLARIAN | Renewable Energy Consulting" />
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700 mb-1 block">Site Name</label>
            <Input value={data.siteName || ''} onChange={e => update('siteName', e.target.value)} data-testid="site-name-input" />
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700 mb-1 block">Company Full Name</label>
            <Input value={data.companyFullName || ''} onChange={e => update('companyFullName', e.target.value)} />
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700 mb-1 block">Email</label>
            <Input value={data.email || ''} onChange={e => update('email', e.target.value)} />
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700 mb-1 block">Phone</label>
            <Input value={data.phone || ''} onChange={e => update('phone', e.target.value)} />
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700 mb-1 block">WhatsApp Link</label>
            <Input value={data.whatsapp || ''} onChange={e => update('whatsapp', e.target.value)} />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Counter Stats (Homepage)</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium text-gray-700 mb-1 block">Projects MW</label>
            <Input type="number" value={data.counters?.projectsMW || ''} onChange={e => update('counters.projectsMW', Number(e.target.value))} />
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700 mb-1 block">PPAs Delivered</label>
            <Input type="number" value={data.counters?.ppasDelivered || ''} onChange={e => update('counters.ppasDelivered', Number(e.target.value))} />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Social Media Links</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {['linkedin', 'youtube', 'instagram', 'twitter', 'facebook', 'whatsapp'].map(platform => (
            <div key={platform}>
              <label className="text-sm font-medium text-gray-700 mb-1 block capitalize">{platform}</label>
              <Input value={data.socialLinks?.[platform] || ''} onChange={e => update(`socialLinks.${platform}`, e.target.value)} />
            </div>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Office Addresses</CardTitle></CardHeader>
        <CardContent className="space-y-6">
          {['head', 'regional'].map(office => (
            <div key={office} className="space-y-3 p-4 bg-gray-50 rounded-lg">
              <h4 className="font-semibold capitalize">{office} Office</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Name</label>
                  <Input value={data.offices?.[office]?.name || ''} onChange={e => update(`offices.${office}.name`, e.target.value)} />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Google Maps URL</label>
                  <Input value={data.offices?.[office]?.mapUrl || ''} onChange={e => update(`offices.${office}.mapUrl`, e.target.value)} />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Full Address</label>
                <Input value={data.offices?.[office]?.address || ''} onChange={e => update(`offices.${office}.address`, e.target.value)} />
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      <Button onClick={save} disabled={saving} className="w-full" data-testid="save-site-settings">
        {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
        Save Site Settings
      </Button>
    </div>
  );
};

export default SiteSettingsEditor;
